import { formatDurationAmount, formatThresholdAmount } from '../components/shared/severity-sensitivity/severity-sensitivity.util';
import { Signal, SignalTextPart, SignalUserBar } from '../models';

/**
 * Computed Needs you body copy for the alert types whose card content is built from structured
 * Signal fields (scope, direction, streak, teamSize...) rather than hand-typed per fixture like
 * every other alert type's `parts` — see AA-124's Signal Card spec. One template per alert type
 * covers both team and user scope from the same payload shape.
 *
 * Every citation's label and URL is the alert type's fixed "learn more" destination, not data, so
 * they're literals here rather than Signal fields.
 */
// Exported: AA-214's person cards reuse these same per-type destinations for their own real,
// per-user/per-day citation links — see ask-ai-person-signals.util.ts's buildPersonCardSignalLink.
export const ACTIVITY_SUMMARY_URL = '/activity-summary';
export const HOURS_TRACKED_URL = '/hours-tracked';
export const TIMELINE_URL = '/timeline';
export const EDIT_TIME_URL = '/edit-time';
export const ATTENDANCE_URL = '/attendance';

// formatThresholdAmount only special-cases the literal '%' symbol — normalize the word form first
// (same pair metricToUnit, in ask-ai-mock-data.ts, maps for the backend's own 'percentage' metric
// key) so a unit spelled out as a word still renders as "72%", not "72 percent".
function normalizeUnit(unit: string): string {
  return unit === 'percent' || unit === 'percentage' ? '%' : unit;
}

// The response's own "8h 9m" text for this bar (see SignalUserBar.display), verbatim, when it
// sent one — only unusual_activity/manual_time/mobile_time's bars carry it; anything else falls
// back to reformatting the numeric value.
function barAmount(bar: SignalUserBar): string {
  return bar.display ?? formatDurationAmount(bar.value);
}

/** 1 -> '1st', 2 -> '2nd', 3 -> '3rd', 4 -> '4th', 11-13 -> 'th' (not 'st'/'nd'/'rd'). */
export function ordinal(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

const WORD_ORDINALS = ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

/** 1 -> 'first', 2 -> 'second', ... 10 -> 'tenth'; falls back to the numeral ordinal past that
 * (heavy_hours' team streak sentence reads as "a second week running", not "a 2nd week running"). */
export function wordOrdinal(n: number): string {
  return WORD_ORDINALS[n] || ordinal(n);
}

/** Comma/and-joins any number of items: "A", "A and B", "A, B and C" — not just a pair, per
 * AA-124's acceptance criteria. Shared with NeedsYouBriefComponent.signalSummary. */
export function joinWithAnd(items: string[]): string {
  if (items.length <= 1) return items[0] || '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** Distinct alert-type labels across critical/warning cards — dedupes on `types` (falling back to
 * `[type]`), not `type` itself, since `type` is already a joined "X and Y" string for a person card
 * (AA-214) and deduping on it would misread two people sharing a type as different topics. */
export function urgentSignalTypes(signals: Signal[]): string[] {
  return Array.from(new Set(
    signals.filter(s => s.sev === 'critical' || s.sev === 'warning').flatMap(s => s.types ?? [s.type]),
  ));
}

/** Arithmetic mean of the bars' values — 0 for an empty list, so callers don't need a guard. */
function average(values: number[]): number {
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : 0;
}

/**
 * Builds idle_time's Needs you body: line 1 states the value against the threshold (worded for
 * whichever direction the alert is configured with), line 2 is scope-specific — how many members
 * are over the line for a team, or the streak for a single user (omitted entirely at streak 1,
 * not just left empty). `when` is the same date-range phrase signalTitle() takes.
 */
export function idleTimeSignalParts(signal: Signal, when: string): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  const values = signal.userBars.map(bar => bar.value);
  // The extreme in whichever direction is the concerning one — the highest value for an
  // above-threshold alert, the lowest for a below-threshold one.
  const value = values.length ? (signal.direction === 'above' ? Math.max(...values) : Math.min(...values)) : 0;

  const unit = normalizeUnit(signal.unit);
  const valueLabel = formatThresholdAmount(value, unit);
  const thresholdLabel = formatThresholdAmount(signal.threshold, unit);
  const line1 = `Idle time on ${scopeLabel} is ${valueLabel} ${when}, ${signal.direction} your ${thresholdLabel} threshold — `;

  const parts: SignalTextPart[] = [
    { text: line1 },
    { link: 'see the idle cells in Activity Summary', url: ACTIVITY_SUMMARY_URL },
    { text: '.' },
  ];

  if (isTeam) {
    parts.push(
      { text: ` ${signal.userCount} members are over the line, and ` },
      { link: 'the per-day pattern sits in Timeline', url: TIMELINE_URL },
      { text: '.' },
    );
  } else if ((signal.streakWeeks ?? 1) > 1) {
    parts.push(
      { text: ` This is the ${ordinal(signal.streakWeeks ?? 1)} week in a row over the line, and ` },
      { link: 'the day-by-day breakdown sits in Timeline', url: TIMELINE_URL },
      { text: '.' },
    );
  }

  return parts;
}

// heavy_hours is only ever evaluated on a weekly cadence today — no per-alert-type lookup needed
// yet, unlike idle_time's `when` (which takes whatever period is actually picked).
const HEAVY_HOURS_PERIOD_LABEL = 'week';

/**
 * Builds heavy_hours' Needs you body: a `break` starts line 2 on its own paragraph, unlike
 * idle_time's line 2 (which just continues inline) — per AA-124's Signal Card spec. Team's line 2
 * always names the evening-hours pattern, adding the streak clause only once there's a real streak
 * to report (streak <= 1 reads as "over for a first week running", which says nothing); user's
 * line 2 is the streak sentence itself, omitted entirely at streak 1 — same convention as
 * idle_time's user body.
 */
export function heavyHoursSignalParts(signal: Signal): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const period = HEAVY_HOURS_PERIOD_LABEL;
  const unit = normalizeUnit(signal.unit);
  const thresholdLabel = formatThresholdAmount(signal.threshold, unit);
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  const streakWeeks = signal.streakWeeks ?? 1;

  if (isTeam) {
    // A per-user list ("at 52h, 50h and 49h...") gets unreadable as the team grows, and the
    // average stays a single figure regardless of headcount — see AA-124 follow-up decision to
    // show the average here rather than a min/max range.
    const avgLabel = formatThresholdAmount(average(signal.userBars.map(bar => bar.value)), unit);
    const teamLine1 = `Weekly tracked time for ${signal.userCount} members is ${signal.direction} your `
      + `${thresholdLabel} threshold, averaging ${avgLabel} — `;
    const evening = streakWeeks > 1
      ? `All ${signal.userCount} have been over for a ${wordOrdinal(streakWeeks)} ${period} running, and the evening hours show up in `
      : 'The evening hours show up in ';
    return [
      { text: teamLine1 },
      { link: `see the ${period} in Hours Tracked`, url: HOURS_TRACKED_URL },
      { text: '.' },
      { break: true },
      { text: evening },
      { link: `Timeline, ${scopeLabel}, this ${period}`, url: TIMELINE_URL },
      { text: ' as well.' },
    ];
  }

  const value = signal.userBars[0]?.value ?? 0;
  const valueLabel = formatThresholdAmount(value, unit);
  const line1 = `${scopeLabel} tracked time this ${period} is ${valueLabel}, ${signal.direction} your ${thresholdLabel} threshold — `;
  const parts: SignalTextPart[] = [
    { text: line1 },
    { link: `see the ${period} in Hours Tracked`, url: HOURS_TRACKED_URL },
    { text: '.' },
  ];
  if (streakWeeks > 1) {
    parts.push(
      { break: true },
      { text: `This is the ${ordinal(streakWeeks)} ${period} in a row over the line, and the evening hours show up in ` },
      { link: `Timeline, ${scopeLabel}, this ${period}`, url: TIMELINE_URL },
      { text: ' as well.' },
    );
  }
  return parts;
}

export const UNUSUAL_ACTIVITY_URL = '/unusual-activity';

/**
 * Builds unusual_activity's Needs you body — hours of unusual activity against the threshold,
 * same as every other numeric field on this alert type (signal.unit is 'h', matching what the
 * existing threshold chart already plots). Team is a single paragraph pair like heavy_hours':
 * line 1 states the flagged members' hours, line 2 (its own paragraph) names what they share —
 * omitted entirely when signal.sharedPatternLabel isn't set, same "omit, don't guess" convention
 * as the streak sentences below. User scope has two variants, chosen by signal.evaluationPeriod
 * (defaults to 'week'): weekly reads like idle_time/heavy_hours' user body (streak sentence,
 * omitted at streak 1); daily instead compares today against the rest of the week, since a
 * single-day spike doesn't have a meaningful week-over-week streak.
 */
export function unusualActivitySignalParts(signal: Signal): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  // unusual_activity's ratio is a duration (see Signal.durationRatio) — "8h 09m", not "8.2h".
  const thresholdLabel = formatDurationAmount(signal.threshold);

  if (isTeam) {
    // A per-member list ("at 12h, 9h and 7h...") gets unreadable as the team grows — see
    // heavyHoursSignalParts' same AA-124 follow-up decision to show the average instead.
    const avgLabel = formatDurationAmount(average(signal.userBars.map(bar => bar.value)));
    const teamLine1 = `Unusual activity for ${signal.userCount} members is above your `
      + `${thresholdLabel} threshold, averaging ${avgLabel} — `;
    const teamParts: SignalTextPart[] = [
      { text: teamLine1 },
      { link: 'review it in Unusual Activity', url: UNUSUAL_ACTIVITY_URL },
      { text: '.' },
    ];
    // Omitted entirely, not left empty, when there's no shared pattern to name — "same undefined"
    // is worse than saying nothing, and unlike the streak clauses below there's no sensible
    // fallback sentence for a team card with nothing in common to point out.
    if (signal.sharedPatternLabel) {
      teamParts.push(
        { break: true },
        { text: `All ${signal.userCount} had most of theirs on the same ${signal.sharedPatternLabel}, and the pattern shows up in ` },
        { link: `Timeline, ${scopeLabel}, this week`, url: TIMELINE_URL },
        { text: ' as well.' },
      );
    }
    return teamParts;
  }

  const valueLabel = signal.userBars[0] ? barAmount(signal.userBars[0]) : formatDurationAmount(0);

  if (signal.evaluationPeriod === 'day') {
    const dayLine1 = `${scopeLabel} has ${valueLabel} of unusual activity today, above your ${thresholdLabel} threshold — `;
    return [
      { text: dayLine1 },
      { link: 'review it in Unusual Activity', url: UNUSUAL_ACTIVITY_URL },
      { text: '.' },
      { break: true },
      { text: 'This is the highest total this week, and the timestamps sit in ' },
      { link: `Timeline, ${scopeLabel}, today`, url: TIMELINE_URL },
      { text: '.' },
    ];
  }

  const weekLine1 = `${scopeLabel} has ${valueLabel} of unusual activity this week, above your ${thresholdLabel} threshold — `;
  const parts: SignalTextPart[] = [
    { text: weekLine1 },
    { link: 'review it in Unusual Activity', url: UNUSUAL_ACTIVITY_URL },
    { text: '.' },
  ];
  if ((signal.streakWeeks ?? 1) > 1) {
    parts.push(
      { break: true },
      { text: `This is the ${ordinal(signal.streakWeeks ?? 1)} week in a row over the line, and most of it clusters in ` },
      { link: `Timeline, ${scopeLabel}, this week`, url: TIMELINE_URL },
      { text: '.' },
    );
  }
  return parts;
}

/**
 * Builds manual_time's Needs you body — hours of manual time against the threshold (signal.unit
 * is 'h', same as heavy_hours), same shape as heavy_hours' body throughout: `break`-started line
 * 2, team's streak clause falling back to a plain sentence when there's no real streak to report,
 * user's streak sentence omitted at streak 1. Team's line 1 counts flagged members, not teams,
 * despite AA-124's "N teams" wording — this alert type's own fixture (two named people, not two
 * teams) and every sibling alert type's team body both count members, so "teams" there reads as a
 * copy slip rather than a real team-of-teams scope.
 */
export function manualTimeSignalParts(signal: Signal): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  // manual_time's ratio is a duration (see Signal.durationRatio) — "8h 09m", not "8.2h".
  const thresholdLabel = formatDurationAmount(signal.threshold);
  const streakWeeks = signal.streakWeeks ?? 1;

  if (isTeam) {
    // A per-member list ("at 8h, 7h and 5h...") gets unreadable as the team grows — see
    // heavyHoursSignalParts' same AA-124 follow-up decision to show the average instead.
    const avgLabel = formatDurationAmount(average(signal.userBars.map(bar => bar.value)));
    const teamLine1 = `Manual time entries for ${signal.userCount} members are ${signal.direction} your `
      + `${thresholdLabel} threshold, averaging ${avgLabel} — `;
    const breakdown = streakWeeks > 1
      ? `All ${signal.userCount} have been over for a ${wordOrdinal(streakWeeks)} week running, and the breakdown sits in `
      : 'The breakdown sits in ';
    return [
      { text: teamLine1 },
      { link: 'see the entries in Edit Time', url: EDIT_TIME_URL },
      { text: '.' },
      { break: true },
      { text: breakdown },
      { link: `Activity Summary, ${scopeLabel}, this week`, url: ACTIVITY_SUMMARY_URL },
      { text: ' as well.' },
    ];
  }

  const valueLabel = signal.userBars[0] ? barAmount(signal.userBars[0]) : formatDurationAmount(0);
  const line1 = `${scopeLabel}'s manual time this week is ${valueLabel}, ${signal.direction} your ${thresholdLabel} threshold — `;
  const parts: SignalTextPart[] = [
    { text: line1 },
    { link: 'see the entries in Edit Time', url: EDIT_TIME_URL },
    { text: '.' },
  ];
  if (streakWeeks > 1) {
    parts.push(
      { break: true },
      { text: `This is the ${ordinal(streakWeeks)} week in a row over the line, and the breakdown sits in ` },
      { link: `Activity Summary, ${scopeLabel}, this week`, url: ACTIVITY_SUMMARY_URL },
      { text: '.' },
    );
  }
  return parts;
}

/**
 * Builds mobile_time's Needs you body — hours of mobile time against the threshold (signal.unit
 * is 'h'). Same shape as manualTimeSignalParts throughout, including its two generalizations from
 * AA-124's literal wording: "All {N}" rather than a hardcoded "Both" in the team streak clause,
 * and citation2's label holding just the destination ("Activity Summary, {scope}, this week"),
 * not the surrounding "the breakdown sits in ..." — the body template already supplies that verb
 * phrase, so a citation2Label that repeated it would double up ("sits in the breakdown sits in
 * Activity Summary").
 */
export function mobileTimeSignalParts(signal: Signal): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  // mobile_time's ratio is a duration (see Signal.durationRatio) — "8h 09m", not "8.2h".
  const thresholdLabel = formatDurationAmount(signal.threshold);
  const streakWeeks = signal.streakWeeks ?? 1;

  if (isTeam) {
    // A per-member list ("at 9h, 6h and 5h...") gets unreadable as the team grows — see
    // heavyHoursSignalParts' same AA-124 follow-up decision to show the average instead.
    const avgLabel = formatDurationAmount(average(signal.userBars.map(bar => bar.value)));
    const teamLine1 = `Mobile time for ${signal.userCount} members is ${signal.direction} your `
      + `${thresholdLabel} threshold, averaging ${avgLabel} — `;
    const breakdown = streakWeeks > 1
      ? `All ${signal.userCount} have been over for a ${wordOrdinal(streakWeeks)} week running, and the breakdown sits in `
      : 'The breakdown sits in ';
    return [
      { text: teamLine1 },
      { link: 'see the entries in Edit Time', url: EDIT_TIME_URL },
      { text: '.' },
      { break: true },
      { text: breakdown },
      { link: `Activity Summary, ${scopeLabel}, this week`, url: ACTIVITY_SUMMARY_URL },
      { text: ' as well.' },
    ];
  }

  const valueLabel = signal.userBars[0] ? barAmount(signal.userBars[0]) : formatDurationAmount(0);
  const line1 = `${scopeLabel}'s mobile time this week is ${valueLabel}, ${signal.direction} your ${thresholdLabel} threshold — `;
  const parts: SignalTextPart[] = [
    { text: line1 },
    { link: 'see the entries in Edit Time', url: EDIT_TIME_URL },
    { text: '.' },
  ];
  if (streakWeeks > 1) {
    parts.push(
      { break: true },
      { text: `This is the ${ordinal(streakWeeks)} week in a row over the line, and the breakdown sits in ` },
      { link: `Activity Summary, ${scopeLabel}, this week`, url: ACTIVITY_SUMMARY_URL },
      { text: '.' },
    );
  }
  return parts;
}

/**
 * Builds attendance_issues' Needs you body — hours of late-start time against the threshold
 * (signal.unit is 'h', same as heavy_hours/manual_time/mobile_time), matching the existing
 * fixtures' own "was late by 7h 13m" framing. Team's line 2 has no streak variant in the brief
 * (unlike heavy_hours/manual_time/mobile_time) — it's unconditional, just naming that the group
 * was late on the same days, generalized from a hardcoded "Both" to "All {N}". User's line 2 is
 * the ordinal streak sentence, omitted at streak 1 — same convention as every sibling body.
 */
export function attendanceIssuesSignalParts(signal: Signal): SignalTextPart[] {
  const isTeam = signal.userCount !== 1;
  const scopeLabel = isTeam ? (signal.teamLabel || `${signal.userCount} users`) : (signal.userName || '');
  const unit = normalizeUnit(signal.unit);
  const thresholdLabel = formatThresholdAmount(signal.threshold, unit);

  if (isTeam) {
    // A per-member list ("at 8h, 6h and 4h...") gets unreadable as the team grows — see
    // heavyHoursSignalParts' same AA-124 follow-up decision to show the average instead.
    const avgLabel = formatThresholdAmount(average(signal.userBars.map(bar => bar.value)), unit);
    const teamLine1 = `Late-start hours for ${signal.userCount} members are ${signal.direction} your `
      + `${thresholdLabel} threshold, averaging ${avgLabel} — `;
    return [
      { text: teamLine1 },
      { link: 'see the days in Attendance', url: ATTENDANCE_URL },
      { text: '.' },
      { break: true },
      { text: `All ${signal.userCount} were late on the same days this week, and the pattern sits in ` },
      { link: `Timeline, ${scopeLabel}, this week`, url: TIMELINE_URL },
      { text: ' as well.' },
    ];
  }

  const value = signal.userBars[0]?.value ?? 0;
  const valueLabel = formatThresholdAmount(value, unit);
  const line1 = `${scopeLabel} was late by ${valueLabel} this week, ${signal.direction} your ${thresholdLabel} threshold — `;
  const parts: SignalTextPart[] = [
    { text: line1 },
    { link: 'see the days in Attendance', url: ATTENDANCE_URL },
    { text: '.' },
  ];
  if ((signal.streakWeeks ?? 1) > 1) {
    parts.push(
      { break: true },
      { text: `This is the ${ordinal(signal.streakWeeks ?? 1)} week in a row over the line, and the pattern sits in ` },
      { link: `Timeline, ${scopeLabel}, this week`, url: TIMELINE_URL },
      { text: '.' },
    );
  }
  return parts;
}

const REPORT_NAME_BY_URL: Record<string, string> = {
  [ACTIVITY_SUMMARY_URL]: 'Activity Summary',
  [HOURS_TRACKED_URL]: 'Hours Tracked',
  [TIMELINE_URL]: 'Timeline',
  [EDIT_TIME_URL]: 'Edit Time',
  [ATTENDANCE_URL]: 'Attendance',
  [UNUSUAL_ACTIVITY_URL]: 'Unusual Activity',
};

/** Human report name for one of the citation URLs above — used for tracking the "Report Link
 * Clicked" event, not rendering. Falls back to the raw url so a future citation destination
 * doesn't silently drop out of tracking while still working for the user. */
export function reportNameForUrl(url?: string): string {
  return (url && REPORT_NAME_BY_URL[url]) || url || '';
}
